import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FloatingButton } from "react-draggable-float-btn";
import { CheckIcon } from '@heroicons/react/24/solid';
import { Check, X, Loader2, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { useUser } from '@/contexts/UserContext';
import { subscriptionService, type PlanFeature, type SubscriptionPackage } from '@/services/subscriptionService';
import { toast } from 'react-toastify';

const PlanSelectionPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logedin } = useUser();
  
  // Get state from navigation (coming from OTP verification)
  const { requires_payment, plan } = location.state || {};

  useEffect(() => {
    // If user is already subscribed and doesn't require payment, redirect to dashboard
    if (!requires_payment && user) {
      navigate('/dashboard');
    }
    fetchPackages();
  }, [requires_payment, user, navigate]);

  // Update body class based on layout
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      // const xxl = mainElement?.classList.contains('2xl:w-1/3');
      // const xl = mainElement?.classList.contains('xl:w-2/5');
      // const lg = mainElement?.classList.contains('lg:w-1/2');
      // const md = mainElement?.classList.contains('md:w-3/5');
      // const full = mainElement?.classList.contains('w-full');
      
      mainElement?.classList.remove('2xl:w-1/3', 'xl:w-2/5', 'lg:w-1/2', 'md:w-3/5');      
      mainElement?.classList.add('w-full');
    }

    // Cleanup on unmount
    return () => {
      if (mainElement) mainElement?.classList.add('2xl:w-1/3', 'xl:w-2/5', 'lg:w-1/2', 'md:w-3/5', 'w-full');
    };
  }, [location.pathname]);

  const fetchPackages = async () => {
    try {
      setIsLoading(true);
      // Using the same endpoint as SubscriptionPlan component
      const response = await subscriptionService.getPackages();
      setPackages(response.packages || []);
    } catch (error: any) {
      console.error('Failed to fetch packages:', error);
      toast.error(error.response?.data?.message || "Failed to load subscription packages.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (pkg: SubscriptionPackage) => {
    setIsSubscribing(pkg.id.toString());

    try {
      // Check if this is a free plan (price is 0)
      const isFreePlan = pkg.price === 0 || 
                        pkg.price === 'RM0.00' || 
                        pkg.price === 'RM0' || 
                        pkg.name.toLowerCase().includes('free trial') || 
                        !pkg.stripe_price_id;

      if (isFreePlan) {
        // Activate free plan directly
        const response = await subscriptionService.activateFreeTrial({
          subscription_package_id: pkg.id,
        });

        toast.success(response?.message || "Free plan activated successfully!");

        // Update user context if needed
        if (user) {
          // You might want to refresh user data here
        }

        // Redirect to dashboard
        navigate('/dashboard');
        return;
      }

      // For paid plans, use Stripe checkout
      if (!pkg.stripe_price_id) {
        toast.error("Stripe price ID not found for this package.");
        return;
      }

      const response = await subscriptionService.createSubscriptionCheckout({
        price_id: pkg.stripe_price_id,
        subscription_package_id: pkg.id,
        allow_multiple: false,
        source: 'plan-selection',
      });

      // Redirect to Stripe checkout
      if (response?.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('Checkout URL not received');
      }
    } catch (error: any) {
      console.error('Failed to subscribe:', error);
      toast.error(error.response?.data?.message || "Failed to start subscription process. Please try again.");
    } finally {
      setIsSubscribing(null);
    }
  };

  const handleSkip = () => {
    // User chooses to stay on free plan - redirect to dashboard
    navigate('/dashboard');
  };

  // Helper function to check if package is PLUS
  const isPlusPlan = (packageName: string, payId: string|null = null): boolean => {
    const nameLower = packageName.toLowerCase();
    return nameLower.includes('plus') || nameLower.includes('premium') || !!payId;
  };

  const isNumeric = (str: any) => {
    // Check if the input is a string and not empty before conversion
    if (typeof str != "string" || str.trim() === '') return false;

    // Use the unary plus (+) to attempt conversion to a number,
    // then check if the result is a finite number
    return Number.isFinite(+str);
  };

  // Format price to display format
  const formatPrice = (price: number|string|null): string => {
    return isNumeric(price) ? `RM${Number(price|| 0).toFixed(2)}` : (price?.toString() || '');
  };

  // Format duration text
  const formatDuration = (validityDays: number): string => {
    const months = Math.round(validityDays / 30);
    if (months === 1) return '1 Month';
    if (months === 3) return '3 Months';
    if (months === 12) return '1 Year';
    return `${validityDays} Days`;
  };

  // Get features based on package
  const getPackageFeatures = (pkg: SubscriptionPackage): PlanFeature[] => {

    if ('features' in pkg && pkg.features) {
      return pkg.features;
    }

    const isPlus = isPlusPlan(pkg.name);
    // const validityMonths = Math.round(pkg.validity_days / 30);
      
    // Get quota values from API, default to 0 if not provided
    const careerCoachingQuota = pkg.career_coaching_quota ?? 0;
    const professionalNetworkQuota = pkg.professional_network_building_quota ?? 0;
    
    // Format quota text for career coaching
    const careerCoachingText = careerCoachingQuota > 0 
      ? `${careerCoachingQuota}-time Career \n Coaching Session`
      : 'Career \n Coaching Session';
    
    // Format quota text for professional network building
    const professionalNetworkText = professionalNetworkQuota > 0
      ? `${professionalNetworkQuota}-time Professional \n Network Building`
      : 'Professional \n Network Building';
    
    // Default/Other subscriptions (non-PLUS or other durations)
    if (isPlus) {
      return [
        { text: 'Full Access to All Skillstree Microlearning', available: true },
        { text: careerCoachingText, available: careerCoachingQuota > 0 },
        { text: professionalNetworkText, available: professionalNetworkQuota > 0 },
        { text: 'Earn Microlearning Digital Badge', available: true },
        { text: 'Earn Digital Microlearning Role Passport', available: true }
      ];
    } else {
      return [
        { text: 'Partial Access to One \n Skillstree Microlearning', available: true },
        { text: careerCoachingText, available: careerCoachingQuota > 0 },
        { text: professionalNetworkText, available: professionalNetworkQuota > 0 },
        { text: 'Earn Microlearning Digital Badge', available: true },
        { text: 'Earn Digital Microlearning \n Role Passport', available: true }
      ];
    }
  };

  // Filter out renewal-only packages and sort by price
  const availablePlans = packages
    .filter(pkg => !pkg.only_for_renewal)
    .sort((a, b) => Number(a.price || 0) - Number(b.price || 0));

  const countPlans = availablePlans.length;

  const colStart = countPlans > 3 ? 0 : 4 - countPlans;

  // If no payment required, don't show this page
  if (!requires_payment && user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 auth-background">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Redirecting...</h1>
          <p className="text-white">Taking you to your dashboard.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 auth-background">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white mr-3" />
          <span className="text-white text-lg">Loading plans...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 auth-background py-8">
      <div className="max-w-6xl w-full pb-14 sm:pb-0">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-white mb-2">Start Your Plan</h1>
          {/* <p className="text-white text-lg">Choose your plan to get started</p> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-6 mb-8">
          {availablePlans.map((pkg, idx) => {
            const isPlus = isPlusPlan(pkg.name, pkg.stripe_price_id);
            const isRecommended = pkg.validity_days >= 85 && pkg.validity_days <= 95; // 3-month plan
            const features = getPackageFeatures(pkg);
            const isFree = pkg.price === 0;

            let planLogoSrc = `/navbar-logo-latest-white.png`;
            let planLogoAlt = `SKILLSTURE`;
            let planLogoCls = `h-10`;

            if (isRecommended) {
              planLogoSrc = `/navbar-logo-latest-blue.png`;
              planLogoAlt = `${planLogoAlt} Recommended`;
            }

            if (isPlus) {
              planLogoSrc = isRecommended ? `/navbar-logo-plus-blue.png` : `/navbar-logo-plus-white.png`;
              planLogoAlt = `${planLogoAlt} PLUS`;
              planLogoCls = `h-16`;
            }

            // Determine column start class based on index and count
            let colStartClass = '';
            if (countPlans === 1) {
              colStartClass = 'lg:col-start-2'; // Center single plan
            } else if (countPlans === 2 && idx === 0) {
              colStartClass = 'lg:col-start-2'; // First of two plans
            }

            return (
              <Card 
                key={pkg.id}
                className={`rounded-lg border-2 relative flex flex-col h-full ${
                  isRecommended 
                    ? 'border-blue-500 transform scale-105' 
                    : 'border-gray-600'
                } ${colStartClass}`}
                style={{
                  backgroundColor: isRecommended ? '#FFFFFF' : '#6A6A6A59',
                  boxShadow: 'inset 0 0 10px rgba(96, 96, 96, 1)',
                }}
              >
                {isRecommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <img 
                      src={planLogoSrc}
                      alt={planLogoAlt} 
                      className={`${planLogoCls} w-auto object-cover`}
                      onError={(e) => e.currentTarget.src = '/navbar-logo.png'}
                    />
                    {/* {isPlus && (
                      <div className="skillsture-plus-badge">
                        <span className="text-white text-xs font-medium">PLUS</span>
                      </div>
                    )} */}
                  </div>
                  
                  <div className="text-center mt-4">
                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-2xl font-semibold ${isRecommended ? 'text-black' : 'text-white'}`}>
                        {formatPrice(pkg.price || 0)}
                      </span>
                    </div>
                    {pkg.original_price && pkg.original_price > Number(pkg.price || 0) && (
                      <div className={`${isRecommended ? 'text-gray-600' : 'text-gray-400'} text-sm line-through mt-1`}>
                        {formatPrice(pkg.original_price || 0)}
                      </div>
                    )}
                    <div className={`text-sm mt-1 ${isRecommended ? 'text-black' : 'text-white'}`}>
                      {formatDuration(pkg.validity_days)}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 flex flex-col px-6">
                  <div className="space-y-3 flex-1 mb-6">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          feature.available 
                            ? 'bg-green-500 text-white' 
                            : 'bg-red-500 text-white'
                        }`}>
                          {feature.available ? (
                            <Check className="w-3 h-3"/>
                          ) : (
                            <X className="w-3 h-3"/>
                          )}
                        </div>
                        <span className={`text-sm leading-relaxed ${
                          feature.available 
                            ? (isRecommended ? 'text-black' : 'text-white')
                            : 'text-gray-400 line-through'
                        }`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4">
                    <Button 
                      onClick={() => handleSubscribe(pkg)}
                      disabled={isSubscribing === pkg.id.toString()}
                      className={`w-full h-10 text-sm ${
                        isRecommended
                          ? 'bg-blue-600 hover:bg-blue-700 text-white scale-105'
                          : isFree
                          ? 'bg-gray-600 hover:bg-gray-700 text-white'
                          : 'bg-orange-500 hover:bg-orange-600 text-white'
                      }`}
                    >
                      {isSubscribing === pkg.id.toString() ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : isFree ? (
                        'Get Started Free'
                      ) : (
                        'Subscribe Now'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Skip option for free plan */}

        <p className="text-gray-200 text-xs text-center mt-4">
          You can upgrade to premium anytime from your dashboard
        </p>
      </div>

      {/* Draggable Floating Button for Plan validity and subscription */}
      <FloatingButton
        onClick={handleSkip}
        position={{
          x: window.innerWidth - (window.innerWidth > 767 ? 150 : 125), 
          y: window.innerHeight - 100
        }}
        style={{
          borderRadius: '18px',
          width: '7rem',
          height: '2.5rem',
          cursor: 'pointer',
          zIndex: 1000
        }}
      >
        <span className='text-orange-600 font-medium'>Skip </span>
        <SkipForward className='text-orange-600 -mt-1 ms-1 h-4'/>
      </FloatingButton>
    </div>
  );
};

export default PlanSelectionPage;